/** Column definitions for the orders grid.
 *
 * All columns are `filterable: false` — filtering is driven entirely by the
 * QuerySearch widget, not per-column grid filters (the page also sets
 * `disableColumnFilter`). Columns hidden by default (see OrdersPage's
 * DEFAULT_HIDDEN: supplier_ref, reseller_ref, custom_id, integration, price,
 * start/end datetime, raw created/updated) are controlled via the presets hook's
 * column-visibility model, NOT a column-level `hide` prop.
 *
 * The Tags column shows the ORDER's own tags (CP-8095) — facts about the whole
 * order — which are distinct from its bookings' tags. The `tag`/`category`
 * namespaces still search booking-level tags; `order_tag`/`order_category`
 * search these.
 *
 * Cross-links: the Bookings cell links each tour to its console tour page and
 * each date/time to the Bookings grid pre-filtered to that booking; the Order #
 * cell links to the Bookings grid filtered by that order reference. Links are
 * built by serialising a QuerySearch `FilterNode[]` into the `?q=` DSL the
 * Bookings page parses.
 */
import EmailIcon from '@mui/icons-material/Email';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, Chip, IconButton, Link, Stack, Tooltip, Typography } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { Link as RouterLink } from 'react-router-dom';

import type { BookingTag } from '@/api/bookings';
import type { OrderBooking, OrderContact, OrderListItem, OrderTenantRef } from '@/api/orders';
import { CellActionsPopover, type CellAction } from '@/components/DataGrid/CellActionsPopover';
import { TimestampsCell, type TimestampsReplaceFn } from '@/components/DataGrid/TimestampsCell';
import { serialize, type FilterNode } from '@/components/QuerySearch';
import { TagBadgeSet } from '@/components/TagBadgeSet/TagBadgeSet';
import { TenantMark } from '@/components/TenantMark/TenantMark';
import { formatDate } from '@/utils/format';

import { BookingStateChip } from '../bookings/BookingStateChip';

const mono = { fontFamily: 'monospace', fontSize: 13 } as const;

/** How many booking lines to show inline before collapsing to "+N more". */
const MAX_BOOKING_LINES = 3;

/** Color-coding for the Bookings cell's second line — a colourblind-safe
 *  blue/orange pair so date and time stay distinguishable at a glance. */
const DATE_COLOR = 'info.main';
const TIME_COLOR = 'warning.main';

/** Build a Bookings-grid URL pre-filtered by `nodes`, scoped to `tenantId` (the
 *  Bookings page locks the query to one operator via `?tenant=`). Serialising to
 *  the `?q=` DSL keeps the round-trip identical to a hand-typed query. */
function bookingsHref(tenantId: number | null | undefined, nodes: FilterNode[]): string {
  const usp = new URLSearchParams();
  const q = serialize(nodes);
  if (q) usp.set('q', q);
  if (tenantId != null) usp.set('tenant', String(tenantId));
  const qs = usp.toString();
  return `/bookings${qs ? `?${qs}` : ''}`;
}

/** Format a minor-unit integer amount into a major-unit display string using
 *  the row's currency precision (cents → dollars). Falls back to the raw number
 *  when precision is unknown. Returns null when there's nothing to show. */
export function formatMoney(
  minor: number | null,
  currency: string | null,
  precision: number | null,
): string | null {
  if (minor == null) return null;
  const digits = precision ?? 2;
  const major = minor / 10 ** digits;
  // Intl gives thousands separators + fixed fraction digits; the currency code
  // is appended plainly (not via `style: 'currency'`) because the backend's
  // currency may be any ISO code and we only ever have the code, not a locale.
  const amount = major.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
  return currency ? `${amount} ${currency}` : amount;
}

/** One booking as two lines:
 *    <tour>              → links to the console tour page
 *    <date> <time>       → links to the Bookings grid filtered by tour+date+time
 *  Date and time are colour-coded (see DATE_COLOR / TIME_COLOR). Missing parts
 *  are omitted; a booking with no tour is shown greyed and unlinked. */
function BookingLineBlock({ b, tenantId }: { b: OrderBooking; tenantId: number | null }) {
  const tourName = b.tour?.name ?? 'Unknown tour';
  const time = b.departure?.name ?? null;
  const hasSecondLine = Boolean(b.date || time);

  // The Bookings query this booking's date/time drills into: tour (by id, via
  // the backend's all-digits PK fallback) + exact date + departure time slot.
  const filterNodes: FilterNode[] = [];
  if (b.tour) {
    filterNodes.push({ kind: 'cond', namespace: 'tour', operator: 'iexact', value: String(b.tour.id) });
  }
  if (b.date) filterNodes.push({ kind: 'cond', namespace: 'date', operator: 'exact', value: b.date });
  if (time) filterNodes.push({ kind: 'cond', namespace: 'departure', operator: 'iexact', value: time });

  return (
    <Box sx={{ minWidth: 0 }}>
      {b.tour ? (
        <Link
          component={RouterLink}
          to={`/tours/${b.tour.id}`}
          variant="caption"
          underline="hover"
          title={`Open tour: ${tourName}`}
          sx={{
            display: 'block',
            fontWeight: 600,
            lineHeight: 1.4,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {tourName}
        </Link>
      ) : (
        <Typography
          component="div"
          variant="caption"
          sx={{ fontWeight: 600, lineHeight: 1.4, color: 'text.disabled', whiteSpace: 'nowrap' }}
        >
          {tourName}
        </Typography>
      )}
      {hasSecondLine && (
        <Link
          component={RouterLink}
          to={bookingsHref(tenantId, filterNodes)}
          variant="caption"
          underline="hover"
          title="Show matching bookings"
          sx={{ display: 'block', color: 'inherit', lineHeight: 1.4, whiteSpace: 'nowrap' }}
        >
          {b.date && (
            <Box component="span" sx={{ color: DATE_COLOR, fontVariantNumeric: 'tabular-nums' }}>
              {b.date}
            </Box>
          )}
          {b.date && time ? ' ' : ''}
          {time && (
            <Box component="span" sx={{ color: TIME_COLOR }}>
              {time}
            </Box>
          )}
        </Link>
      )}
    </Box>
  );
}

/** Compact list of an order's bookings — up to MAX_BOOKING_LINES blocks, then a
 *  "+N more" summary. `booking_count` is authoritative for the total (the
 *  `bookings` array itself may already be capped server-side). */
function BookingsCell({ row }: { row: OrderListItem }) {
  const { bookings, booking_count } = row;
  if (booking_count === 0 || bookings.length === 0) {
    return <Box component="span" sx={{ color: 'text.disabled' }}>No bookings</Box>;
  }
  const shown = bookings.slice(0, MAX_BOOKING_LINES);
  const remainder = booking_count - shown.length;
  const tenantId = row.tenant?.id ?? null;
  return (
    <Stack spacing={0.5} sx={{ py: 0.75, minWidth: 0 }}>
      {shown.map((b) => (
        <BookingLineBlock key={b.id} b={b} tenantId={tenantId} />
      ))}
      {remainder > 0 && (
        <Typography component="div" variant="caption" color="text.secondary">
          +{remainder} more
        </Typography>
      )}
    </Stack>
  );
}

/** Contact name (+ company) with email underneath, only when present. Email
 *  opens a quick-actions popover (send) with an inline copy affordance. */
function ContactCell({ contact }: { contact: OrderContact | null }) {
  if (!contact) return <>—</>;
  const name = `${contact.first_name ?? ''} ${contact.last_name ?? ''}`.trim();
  const primary = name || contact.company || '—';
  return (
    <Stack spacing={0.25} sx={{ py: 0.75, minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, minWidth: 0 }}>
        <Typography
          component="span"
          variant="body2"
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {primary}
        </Typography>
        {name && contact.company && (
          <Typography component="span" variant="caption" color="text.disabled" noWrap>
            ({contact.company})
          </Typography>
        )}
      </Box>
      {contact.email && (
        <CellActionsPopover
          auto
          actions={[
            {
              key: 'send',
              label: 'Send email',
              icon: <EmailIcon fontSize="small" />,
              href: `mailto:${contact.email}`,
            },
          ]}
          sx={{
            typography: 'caption',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {contact.email}
        </CellActionsPopover>
      )}
    </Stack>
  );
}

/** Tenant name with a link to the tour-operator page + a badge line (only when
 *  applicable): Franchise/Licensee and the Sandbox mark (D8). */
function TenantCell({ tenant }: { tenant: OrderTenantRef | null }) {
  if (!tenant) return <>—</>;
  const isLicensee = tenant.parent_id != null;
  const isFranchise = !isLicensee && tenant.is_franchise;
  const isSandbox = tenant.environment === 'sandbox';
  const actions: CellAction[] = [
    {
      key: 'open',
      label: 'Open tour operator',
      icon: <OpenInNewIcon fontSize="small" />,
      href: `/tour-operators/${tenant.id}`,
    },
  ];
  return (
    <Stack spacing={0.25} sx={{ py: 0.75, minWidth: 0 }}>
      <Box sx={{ minWidth: 0 }}>
        <CellActionsPopover
          auto
          actions={actions}
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {tenant.name}
        </CellActionsPopover>
      </Box>
      {(isLicensee || isFranchise || isSandbox) && (
        <Stack direction="row" spacing={0.5} sx={{ alignSelf: 'flex-start' }}>
          {isLicensee && (
            <Chip size="small" color="info" label="Licensee" sx={{ height: 18, fontSize: 11 }} />
          )}
          {isFranchise && (
            <Chip
              size="small"
              color="primary"
              label="Franchise"
              sx={{ height: 18, fontSize: 11 }}
            />
          )}
          <TenantMark environment={tenant.environment} />
        </Stack>
      )}
    </Stack>
  );
}

/** A short date (no time) cell for start/end datetimes. */
function ShortDate({ iso }: { iso: string | null }) {
  if (!iso) return <>—</>;
  return (
    <Tooltip title={iso} placement="top" arrow>
      <Box component="span">{formatDate(iso)}</Box>
    </Tooltip>
  );
}

export interface BuildColumnsOptions {
  /** The Tenant column only exists when no tenant is picked above. */
  showTenant: boolean;
  /** Click-to-filter on an order tag chip (adds/removes an `order_tag` term).
   *  Omitted -> chips render but are not clickable. */
  onToggleTag?: (tag: BookingTag) => void;
  /** Whether an `order_tag` term for this tag is currently active. */
  isTagActive?: (tag: BookingTag) => boolean;
  /** Replace/remove the single top-level created|updated range condition — the
   *  Timestamps column's ±delta presets. Omit to render timestamps without
   *  filter actions (e.g. the default column-order computation). */
  onReplaceFilter?: TimestampsReplaceFn;
}

export function buildColumns({
  showTenant,
  onReplaceFilter,
  onToggleTag,
  isTagActive,
}: BuildColumnsOptions): GridColDef<OrderListItem>[] {
  const cols: GridColDef<OrderListItem>[] = [
    {
      field: 'open',
      headerName: '',
      width: 44,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      disableExport: true,
      renderCell: (p) => (
        <Tooltip title="Open order">
          <IconButton
            size="small"
            component={RouterLink}
            to={`/orders/${p.row.id}`}
            aria-label="Open order"
            onClick={(e) => e.stopPropagation()}
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
    {
      field: 'bookings',
      headerName: 'Bookings',
      flex: 1.6,
      minWidth: 240,
      filterable: false,
      sortable: false,
      renderCell: (p) => <BookingsCell row={p.row} />,
    },
  ];

  if (showTenant) {
    cols.push({
      field: 'tenant',
      headerName: 'Tenant',
      flex: 1,
      minWidth: 180,
      filterable: false,
      sortable: false,
      valueGetter: (_v, row) => row.tenant?.name ?? '—',
      renderCell: (p) => <TenantCell tenant={p.row.tenant} />,
    });
  }

  cols.push(
    {
      field: 'order_ref',
      headerName: 'Order #',
      width: 150,
      filterable: false,
      renderCell: (p) => {
        const ref = p.row.order_ref;
        if (!ref) return <Box component="span" sx={mono}>—</Box>;
        return (
          <Link
            component={RouterLink}
            to={bookingsHref(p.row.tenant?.id ?? null, [
              { kind: 'cond', namespace: 'order', operator: 'iexact', value: ref },
            ])}
            underline="hover"
            title={`Show bookings for order ${ref}`}
            sx={{ ...mono }}
          >
            {ref}
          </Link>
        );
      },
    },
    {
      field: 'supplier_ref',
      headerName: 'Supplier ref',
      width: 140,
      filterable: false,
      sortable: false,
      renderCell: (p) => <Box component="span" sx={mono}>{p.row.supplier_ref || '—'}</Box>,
    },
    {
      field: 'reseller_ref',
      headerName: 'Reseller ref',
      width: 140,
      filterable: false,
      sortable: false,
      renderCell: (p) => <Box component="span" sx={mono}>{p.row.reseller_ref || '—'}</Box>,
    },
    {
      field: 'booking_state',
      headerName: 'State',
      width: 150,
      filterable: false,
      sortable: false,
      renderCell: (p) => (
        <BookingStateChip
          booking_state={p.row.booking_state}
          booking_state_display={p.row.booking_state_display}
        />
      ),
    },
    {
      field: 'contact',
      headerName: 'Contact',
      flex: 1.2,
      minWidth: 200,
      filterable: false,
      sortable: false,
      valueGetter: (_v, row) =>
        row.contact
          ? `${row.contact.first_name ?? ''} ${row.contact.last_name ?? ''}`.trim()
          : '',
      renderCell: (p) => <ContactCell contact={p.row.contact} />,
    },
    {
      // Combined integration + restech account (hidden by default). The account
      // name sits on a muted second line when present.
      field: 'integration',
      headerName: 'Integration',
      width: 170,
      filterable: false,
      sortable: false,
      valueGetter: (_v, row) =>
        [row.integration, row.restech_account].filter(Boolean).join(' · ') || '—',
      renderCell: (p) => {
        const integ = p.row.integration;
        const account = p.row.restech_account;
        if (!integ && !account) return '—';
        return (
          <Stack spacing={0} sx={{ py: 0.75, minWidth: 0 }}>
            {integ && (
              <Typography component="span" variant="body2" noWrap>
                {integ}
              </Typography>
            )}
            {account && (
              <Typography component="span" variant="caption" color="text.secondary" noWrap>
                {account}
              </Typography>
            )}
          </Stack>
        );
      },
    },
    {
      field: 'start_datetime',
      headerName: 'Start',
      width: 170,
      filterable: false,
      renderCell: (p) => <ShortDate iso={p.row.start_datetime} />,
    },
    {
      field: 'end_datetime',
      headerName: 'End',
      width: 170,
      filterable: false,
      sortable: false,
      renderCell: (p) => <ShortDate iso={p.row.end_datetime} />,
    },
    {
      field: 'price',
      headerName: 'Price',
      width: 140,
      filterable: false,
      sortable: false,
      align: 'right',
      headerAlign: 'right',
      // Prefer the net price; fall back to retail. Both are minor units.
      renderCell: (p) => {
        const net = formatMoney(p.row.price_net, p.row.currency, p.row.currency_precision);
        const retail =
          net == null
            ? formatMoney(p.row.price_retail, p.row.currency, p.row.currency_precision)
            : null;
        const shown = net ?? retail;
        return <Box component="span" sx={mono}>{shown ?? '—'}</Box>;
      },
    },
    {
      field: 'custom_id',
      headerName: 'Custom ID',
      width: 140,
      filterable: false,
      sortable: false,
      renderCell: (p) => <Box component="span" sx={mono}>{p.row.custom_id || '—'}</Box>,
    },
    {
      // Order.active (already serialized) — inactive = superseded/cancelled order.
      field: 'active',
      headerName: 'Active',
      width: 90,
      filterable: false,
      sortable: false,
      valueGetter: (_v, row) => (row.active ? 'Yes' : 'No'),
    },
    {
      // Combined Created/Updated column (visible default) — mirrors the Bookings
      // grid. Orders carry no create/update source, so the shared cell renders
      // sourceless. The raw created_ts/updated_ts columns below stay for sorting.
      field: 'timestamps',
      headerName: 'Timestamps',
      width: 220,
      filterable: false,
      sortable: false,
      renderCell: (p) => (
        <TimestampsCell
          created={p.row.created_ts}
          updated={p.row.updated_ts}
          onReplaceFilter={onReplaceFilter}
        />
      ),
    },
    {
      field: 'created_ts',
      headerName: 'Created',
      width: 170,
      filterable: false,
      renderCell: (p) =>
        p.row.created_ts ? (
          <Tooltip title={p.row.created_ts} placement="top" arrow>
            <Box component="span">{formatDate(p.row.created_ts)}</Box>
          </Tooltip>
        ) : (
          '—'
        ),
    },
    {
      field: 'updated_ts',
      headerName: 'Updated',
      width: 170,
      filterable: false,
      renderCell: (p) =>
        p.row.updated_ts ? (
          <Tooltip title={p.row.updated_ts} placement="top" arrow>
            <Box component="span">{formatDate(p.row.updated_ts)}</Box>
          </Tooltip>
        ) : (
          '—'
        ),
    },
    {
      field: 'tags',
      headerName: 'Tags',
      flex: 1,
      minWidth: 160,
      filterable: false,
      sortable: false,
      renderCell: (p) => (
        <TagBadgeSet tags={p.row.tags} onToggleTag={onToggleTag} isTagActive={isTagActive} />
      ),
    },
  );

  return cols;
}
